import { useEffect, useRef, useState } from "react";
import { CalendarRange, ChevronDown } from "lucide-react";
import { PRESETS, labelFor } from "@/lib/dateRange";

/**
 * Timeline picker used by the customer dashboard. `onChange({ preset, custom })`.
 * `variant="ops"` renders the light-first ops-console styling.
 */
export const RangeFilter = ({ preset, custom, onChange, variant = "app", testid = "range-filter" }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: custom?.from || "", to: custom?.to || "" });
  const box = useRef(null);

  useEffect(() => {
    const away = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const ops = variant === "ops";
  const trigger = ops
    ? "h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
    : "h-9 px-3 rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700";
  const panel = ops
    ? "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
    : "bg-panel border-zinc-800";
  const item = (active) =>
    ops
      ? `w-full text-left px-3 py-2 text-[13px] rounded-md ${active ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`
      : `w-full text-left px-3 py-2 text-[13px] rounded-md ${active ? "bg-indigo-500/15 text-indigo-300" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"}`;
  const field = ops
    ? "h-9 w-full rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-[13px] text-slate-700 dark:text-slate-200"
    : "h-9 w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-2 text-[13px] text-zinc-200";

  const pick = (id) => {
    if (id === "custom") return setOpen(true);
    onChange({ preset: id, custom: {} });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draft.from || !draft.to) return;
    const ordered = draft.from <= draft.to ? draft : { from: draft.to, to: draft.from };
    onChange({ preset: "custom", custom: ordered });
    setOpen(false);
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 text-xs font-medium transition-colors ${trigger}`}
      >
        <CalendarRange className="w-3.5 h-3.5" />
        <span className="max-w-[160px] truncate">{labelFor(preset, custom)}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          data-testid={`${testid}-menu`}
          className={`absolute right-0 z-40 mt-2 w-60 rounded-xl border p-2 shadow-xl ${panel}`}
        >
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`${testid}-${p.id}`} onClick={() => pick(p.id)} className={item(preset === p.id)}>
              {p.label}
            </button>
          ))}
          <div className="mt-2 border-t border-zinc-800/60 dark:border-slate-800 pt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                data-testid={`${testid}-from`}
                className={field}
                value={draft.from}
                max={draft.to || undefined}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              />
              <input
                type="date"
                data-testid={`${testid}-to`}
                className={field}
                value={draft.to}
                min={draft.from || undefined}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              />
            </div>
            <button
              data-testid={`${testid}-apply`}
              onClick={applyCustom}
              disabled={!draft.from || !draft.to}
              className={`w-full h-9 rounded-md text-xs font-semibold text-white disabled:opacity-50 ${ops ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-500 hover:bg-indigo-600"}`}
            >
              Apply custom range
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeFilter;
